// CHG-1277 / ITER-3402 — v2 ResetPasswordPage.
//
// CHG-2958 (Slice J / UM-R12, partial DRIFT-1153 closure) — this
// hand-written carrier is now the ROUTED implementation again (App.tsx
// /reset-password), replacing the generated twin which cannot branch on
// response fields (tools/v2-gen/v2genauth.py limitation, DRIFT-1153) and
// therefore treated the 200 {mfa_required: true} response as terminal
// success WITHOUT changing the password — a false-success defect.
//
// Backend contract (routers/auth.py reset_password_with_token):
//   * POST /auth/reset-password { token, new_password }
//       - 200 {message}                      → terminal success
//       - 200 {mfa_required: true, mfa_token, mfa_methods} → password NOT
//         changed; prompt for the 6-digit TOTP code and complete via
//         POST /auth/reset-password/mfa-verify { mfa_token, code, new_password }
//       - 400 "Invalid or expired reset token" → unified anti-enumeration
//         terminal detail (CHG-2956 / Slice I) covering expired, consumed,
//         garbage-signature, and wrong-brand tokens. Honest combined copy;
//         no sub-cause claim.
//   * The mfa-verify step 400s on a bad code with the reset token already
//     consumed, so the user must restart from the sign-in page.
//
// Lives OUTSIDE <AppShell>. No ProtectedRoute — token is the auth carrier.

import { useState, type FormEvent } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { apiClient } from "../../api/client";
import { AuthLayout } from "./AuthLayout";
import { usePasswordPolicy } from "./usePasswordPolicy";
import { inputStyle, labelStyle, errorStyle, submitStyle, ctaLinkStyle } from "./styles";

type State =
  | "form"
  | "submitting"
  | "mfa_required"
  | "mfa_submitting"
  | "success"
  | "token_terminal"
  | "error";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [state, setState] = useState<State>("form");
  const [error, setError] = useState<string>("");
  // CHG-2958 — MFA challenge token captured from the 200 {mfa_required}
  // response; the new password is re-submitted with the TOTP code per
  // the backend's two-POST shape (no server-side plaintext cache).
  const [mfaToken, setMfaToken] = useState<string>("");
  const [mfaCode, setMfaCode] = useState<string>("");
  // CHG-2545 / DRIFT-934 — drive the min-length guard from the live
  // server policy (org-configurable, default 12) instead of a hardcoded 8.
  const { policy } = usePasswordPolicy();
  const minLength = policy.min_length;

  function classifyError(e: any, fallback: string): "token_terminal" | "error" {
    const status: number | undefined = e?.response?.status;
    const detail: string | undefined = e?.response?.data?.detail;
    // CHG-2958 — unified anti-enumeration terminal detail (CHG-2956).
    if (
      status === 400 &&
      typeof detail === "string" &&
      /invalid or expired reset token/i.test(detail)
    ) {
      setState("token_terminal");
      return "token_terminal";
    }
    setError(typeof detail === "string" && detail ? detail : fallback);
    setState("error");
    return "error";
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Missing reset token. Request a new password-reset link.");
      setState("error");
      return;
    }
    if (pwd.length < minLength) {
      setError(`Password must be at least ${minLength} characters.`);
      return;
    }
    if (pwd !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setState("submitting");
    try {
      // CHG-2958 — corrected endpoint: the auth router mounts at
      // /api/v1/auth/*; the previous "/reset-password" path 404'd.
      const { data } = await apiClient().post("/auth/reset-password", {
        token,
        new_password: pwd,
      });
      if (data?.mfa_required && data?.mfa_token) {
        setMfaToken(data.mfa_token);
        setState("mfa_required");
        return;
      }
      setState("success");
    } catch (e: any) {
      classifyError(e, "Failed to reset password. Please try again.");
    }
  }

  async function onSubmitMfa(e: FormEvent) {
    e.preventDefault();
    setError("");
    const code = mfaCode.replace(/\s+/g, "");
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setState("mfa_submitting");
    try {
      await apiClient().post("/auth/reset-password/mfa-verify", {
        mfa_token: mfaToken,
        code,
        new_password: pwd,
      });
      setState("success");
    } catch (e: any) {
      // A failed verify consumes the underlying reset token server-side;
      // surface the backend detail (it tells the user to restart) and
      // keep the code field interactive (the "error" classification
      // re-renders the MFA form below with the error banner).
      const kind = classifyError(
        e,
        "MFA verification failed. Please request a new password reset.",
      );
      if (kind === "error") setState("mfa_required");
    }
  }

  if (state === "success") {
    return (
      <AuthLayout pageId="reset-password" title="Password updated">
        <p style={{ fontSize: 14, color: "var(--color-text)" }}>
          Your password has been reset. You may now sign in with your new password.
        </p>
        <Link to="/login" data-action="goto-login" style={ctaLinkStyle()}>
          Continue to sign-in
        </Link>
      </AuthLayout>
    );
  }

  // CHG-2958 — honest combined terminal panel for the unified 400 detail.
  // The backend deliberately does not disclose WHICH failure occurred
  // (anti-enumeration), so the copy claims neither "expired" nor "invalid"
  // alone. v2 has no /forgot-password page; the sign-in page is the
  // re-entry point for requesting a new link.
  if (state === "token_terminal") {
    return (
      <AuthLayout pageId="reset-password" title="Reset link no longer usable">
        <p style={{ fontSize: 14, color: "var(--color-text)" }}>
          This password reset link is invalid, has expired, or was already used.
          Request a new reset link from the sign-in page to continue.
        </p>
        <Link to="/login" data-action="goto-login" style={ctaLinkStyle()}>
          Back to sign-in
        </Link>
      </AuthLayout>
    );
  }

  if (state === "mfa_required" || state === "mfa_submitting") {
    return (
      <AuthLayout
        pageId="reset-password"
        title="Verify it's you"
        subtitle="Two-factor authentication is enabled on this account. Enter the 6-digit code from your authenticator app to finish resetting your password."
      >
        <form onSubmit={onSubmitMfa} data-form="reset-password-mfa">
          <label style={labelStyle}>
            Authenticator code
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              maxLength={6}
              placeholder="000000"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              disabled={state === "mfa_submitting"}
              data-field="mfa-code"
              style={inputStyle}
            />
          </label>
          {error && (
            <div data-role="error" style={errorStyle}>
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={state === "mfa_submitting"}
            data-action="submit-mfa"
            style={submitStyle(state === "mfa_submitting")}
          >
            {state === "mfa_submitting" ? "Verifying…" : "Verify and update password"}
          </button>
        </form>
        <Link to="/login" data-action="goto-login" style={ctaLinkStyle()}>
          Back to sign-in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      pageId="reset-password"
      title="Reset your password"
      subtitle="Enter a new password for your account."
    >
      <form onSubmit={onSubmit} data-form="reset-password">
        <label style={labelStyle}>
          New password
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={minLength}
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            disabled={state === "submitting"}
            data-field="new-password"
            style={inputStyle}
          />
        </label>
        <label style={{ ...labelStyle, marginTop: 12 }}>
          Confirm password
          <input
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={state === "submitting"}
            data-field="confirm-password"
            style={inputStyle}
          />
        </label>
        {error && (
          <div data-role="error" style={errorStyle}>
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={state === "submitting"}
          data-action="submit"
          style={submitStyle(state === "submitting")}
        >
          {state === "submitting" ? "Updating…" : "Update password"}
        </button>
      </form>
    </AuthLayout>
  );
}
